// frontend/src/services/api.js
import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach JWT, block requests while offline ─────────
// Financial app — never even attempt a request when the device has no
// connection. Rejecting here, before dispatch, means the exact same
// response-interceptor handling below (and every page's existing
// toast.error(errMsg(err)) catch block) already does the right thing with
// zero per-page changes — no risk of a stale value ever rendering, and no
// deposit/withdrawal/purchase can be silently attempted offline.
api.interceptors.request.use(
  (config) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const offlineErr = new Error('offline');
      offlineErr.isOfflineError = true;
      return Promise.reject(offlineErr);
    }
    const token = localStorage.getItem('dwi_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: handle errors globally ──────────────
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const isOffline = !error.response && typeof navigator !== 'undefined' && navigator.onLine === false;
    const OFFLINE_MESSAGE = "You're offline — please reconnect to continue.";
    const data = error.response?.data || {};
    // protect returns { message }, merchantApiAuth returns { error } — handle both
    const message = isOffline ? OFFLINE_MESSAGE : (data.message || data.error || 'Something went wrong');

    if (error.response?.status === 401) {
      // Public, session-less endpoints can also legitimately return a
      // message-only 401 for "wrong credential" — e.g. the captive
      // portal's phone+PIN login/purchase, which carries no JWT at all.
      // Without this check, ANY such 401 (a customer simply mistyping
      // their PIN) would be indistinguishable from a real expired-session
      // 401 below and would wipe localStorage + hard-redirect them (or,
      // worse, whoever else's session happens to be in this browser's
      // localStorage) to /login — kicking them out of the public page
      // entirely instead of just showing an inline error. Callers opt in
      // per-request via { skipAuthRedirect: true }.
      if (error.config?.skipAuthRedirect) {
        toast.error(message);
        return Promise.reject({ message, status: 401, data });
      }
      // protect (JWT failure) returns { message } without { error }.
      // merchantApiAuth (wrong key type) returns { error } without { message }.
      // Only log out on true JWT failures — leave the session intact for API-key auth errors.
      const isJwtFailure = !!data.message && !data.error;
      if (isJwtFailure) {
        localStorage.removeItem('dwi_token');
        localStorage.removeItem('dwi_user');
        window.location.href = '/login';
        return;
      }
      // API-key auth failure on a valid session — surface error, do not log out
      toast.error(message);
      return Promise.reject({ message, status: 401, data });
    }

    if (error.response?.status === 403) {
      toast.error(message);
    } else if (error.response?.status === 429) {
      toast.error('Too many requests. Please wait.');
    } else if (!error.response) {
      toast.error(isOffline ? OFFLINE_MESSAGE : 'Network error — check your connection');
    }

    return Promise.reject({ message, status: error.response?.status, data: error.response?.data });
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const authAPI = {
  register:        (data) => api.post('/auth/register', data),
  login:           (data) => api.post('/auth/login', data),
  me:              ()     => api.get('/auth/me'),
  updateProfile:   (data) => api.put('/auth/profile', data),
  changePassword:  (data) => api.put('/auth/change-password', data),
  submitKYC:       (data) => api.post('/auth/kyc', data),
  getReferralData: ()     => api.get('/auth/referral'),
  forgotPassword:      (email)  => api.post('/auth/forgot-password', { email }),
  resetPassword:       (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
  // Phone/SMS-based reset — the primary flow for accounts with an
  // unverified or synthetic email (e.g. captive-portal-created accounts).
  forgotPasswordPhone: (phone)  => api.post('/auth/forgot-password-phone', { phone }),
  resetPasswordPhone:  (data)   => api.post('/auth/reset-password-phone', data),
  sendVerification: ()          => api.post('/auth/send-verification'),
  setup2fa:         ()          => api.post('/auth/setup-2fa'),
  verify2fa:        (token)     => api.post('/auth/verify-2fa', { token }),
  disable2fa:       (password)  => api.delete('/auth/disable-2fa', { data: { password } }),
};

// ── Wallet ────────────────────────────────────────────────────
export const walletAPI = {
  getBalance:         ()         => api.get('/wallet'),
  getRates:           ()         => api.get('/wallet/rates'),
  getTransactions:    (params)   => api.get('/wallet/transactions', { params }),
  // 100s, not the 15s default: Yo!'s synchronous collection call takes ~15-90s
  deposit:            (data)     => api.post('/wallet/deposit', data, { timeout: 100000 }),
  withdraw:           (data)     => api.post('/wallet/withdraw', data),
  withdrawQuote:      (data)     => api.post('/wallet/withdraw/quote', data),
  transfer:           (data)     => api.post('/wallet/transfer', data),
  portfolio:          ()         => api.get('/wallet/portfolio'),
  ledger:             (params)   => api.get('/wallet/ledger', { params }),
  statement:          (params)   => api.get('/wallet/statement.csv', { params, responseType: 'blob' }),
  exchange:           (data)     => api.post('/wallet/exchange', data),
  analytics:          (params)   => api.get('/wallet/analytics', { params }),
  setPin:             (data)     => api.post('/wallet/pin', data),
  verifyPin:          (data)     => api.post('/wallet/pin/verify', data),
  pinStatus:          ()         => api.get('/wallet/pin/status'),
  commissionToPrimary:(data)     => api.post('/wallet/commission-to-primary', data),
};

// ── FX / Currency Exchange ────────────────────────────────────
export const fxAPI = {
  getRates:   ()                    => api.get('/fx/rates'),
  quote:      (from, to, amount)    => api.get('/fx/quote', { params: { from, to, amount } }),
  convert:    (data)                => api.post('/fx/convert', data),
  getHistory: (params)              => api.get('/fx/history', { params }),
};

// ── System Config (admin) ─────────────────────────────────────
export const configAPI = {
  getAll:     (category) => api.get('/config', { params: category ? { category } : {} }),
  get:        (key)      => api.get(`/config/${key}`),
  set:        (key, val) => api.put(`/config/${key}`, { value: val }),
  getStats:   ()         => api.get('/config/stats'),
  getPublic:  ()         => api.get('/config/public'),
};

// ── Fee Rules (admin) ─────────────────────────────────────────
export const feeAPI = {
  getAll:    (params)     => api.get('/fees', { params }),
  calculate: (data)       => api.post('/fees/calculate', data),
  create:    (data)       => api.post('/fees', data),
  update:    (id, data)   => api.put(`/fees/${id}`, data),
  remove:    (id)         => api.delete(`/fees/${id}`),
  simulate:  (params)     => api.get('/fees/simulate', { params }),
};

// ── Feature Flags (public read) ───────────────────────────────
export const featuresAPI = {
  list:   ()          => api.get('/features'),
  toggle: (key)       => api.put(`/features/${key}/toggle`),
  update: (key, data) => api.put(`/features/${key}/toggle`, data),
};

// ── Live Pricing Engine (Phase 11) ───────────────────────────
export const priceAPI = {
  getUsdt:        () => api.get('/price/usdt'),
  getUsdtPublic:  () => api.get('/price/usdt/public'),
  refreshCache:   () => api.post('/price/refresh'),
};

// ── Crypto Withdrawals (Phase 10) ────────────────────────────
export const cryptoWithdrawAPI = {
  getFees:    ()                 => api.get('/crypto-withdraw/fees'),
  getQuote:   (amount, network)  => api.get('/crypto-withdraw/quote', { params: { amount, network } }),
  request:    (data)             => api.post('/crypto-withdraw/request', data),
  getHistory: (params)           => api.get('/crypto-withdraw/history', { params }),
  // Admin
  getQueue:     (params)         => api.get('/crypto-withdraw/queue', { params }),
  getAllOrders:  (params)         => api.get('/crypto-withdraw/all', { params }),
  getTreasury:  ()               => api.get('/crypto-withdraw/treasury'),
  approve:      (id)             => api.post(`/crypto-withdraw/${id}/approve`),
  markSent:     (id, data)       => api.post(`/crypto-withdraw/${id}/mark-sent`, data),
  complete:     (id)             => api.post(`/crypto-withdraw/${id}/complete`),
  reject:       (id, data)       => api.post(`/crypto-withdraw/${id}/reject`, data),
  reconcile:    ()               => api.get('/crypto-withdraw/reconcile'),
};

// ── P2P USDT Exchange (Phase 9) ───────────────────────────────
export const exchangeAPI = {
  getRates:    ()           => api.get('/exchange/rates'),
  getQuote:    (ugxAmount)  => api.get('/exchange/quote', { params: { ugxAmount } }),
  createOrder: (data)       => api.post('/exchange/order', data),
  getHistory:  (params)     => api.get('/exchange/history', { params }),
  // Admin
  getQueue:    (params)     => api.get('/exchange/queue', { params }),
  getAllOrders: (params)     => api.get('/exchange/all', { params }),
  getTreasury: ()           => api.get('/exchange/treasury'),
  approve:     (id)         => api.post(`/exchange/${id}/approve`),
  markSent:    (id, data)   => api.post(`/exchange/${id}/mark-sent`, data),
  reject:      (id, data)   => api.post(`/exchange/${id}/reject`, data),
  getSellQuote:    (amount)     => api.get('/exchange/sell-quote', { params: { amount } }),
  createSellOrder: (data)       => api.post('/exchange/sell-order', data),
  getSettings:     ()           => api.get('/exchange/settings'),
  updateSetting:   (key, value) => api.put(`/exchange/settings/${key}`, { value }),
  getDeskState:    ()           => api.get('/exchange/desk-state'),
  setDeskDirection:(direction, enabled) => api.put('/exchange/desk-state', { direction, enabled }),
};

// ── Investments ───────────────────────────────────────────────
export const investmentAPI = {
  getFunds:         ()          => api.get('/investments/funds'),
  getFund:          (id)        => api.get(`/investments/funds/${id}`),
  invest:           (data)      => api.post('/investments/invest', data),
  getMyInvestments: ()          => api.get('/investments/my'),
  withdrawReturns:  (id)        => api.post(`/investments/${id}/withdraw-returns`),
  processReturns:   (data)      => api.post('/investments/process-returns', data),
  createFund:       (data)      => api.post('/investments/funds', data),
  updateFund:       (id, data)  => api.put(`/investments/funds/${id}`, data),
};

// ── Investment Accounts (Phase 4) ─────────────────────────────
export const investAccountAPI = {
  getProducts:    ()       => api.get('/investments/products'),
  getMyAccounts:  ()       => api.get('/investments/accounts/my'),
  openAccount:    (data)   => api.post('/investments/accounts', data),
  getAccount:     (id)     => api.get(`/investments/accounts/${id}`),
  withdraw:       (id)     => api.post(`/investments/accounts/${id}/withdraw`),
};

// ── Agents ────────────────────────────────────────────────────
export const agentAPI = {
  getProfile:      ()       => api.get('/agents/profile'),
  getFloat:        ()       => api.get('/agents/float'),
  cashIn:          (data)   => api.post('/agents/cash-in', data),
  cashOut:         (data)   => api.post('/agents/cash-out', data),
  searchCustomer:  (q)      => api.get('/agents/customers/search', { params: { q } }),
  getTransactions: (params) => api.get('/agents/transactions', { params }),
  getEODReport:    (date)   => api.get('/agents/eod-report', { params: { date } }),
  requestFloat:    (data)   => api.post('/agents/float/request', data),
};

// ── Merchants ─────────────────────────────────────────────────
export const merchantAPI = {
  // Admin routes (POST /api/merchants, GET /api/merchants, PATCH/:id, etc.)
  adminList:        (params)     => api.get('/merchants', { params }),
  adminCreate:      (data)       => api.post('/merchants', data),
  adminUpdate:      (id, data)   => api.patch(`/merchants/${id}`, data),
  adminSettle:      (id)         => api.post(`/merchants/${id}/settle`),
  adminPayments:    (id, params) => api.get(`/merchants/${id}/payments`, { params }),
  // Merchant portal routes (JWT, /me/* — logged-in merchant only)
  getProfile:       ()       => api.get('/merchants/me'),
  getDashboard:     ()       => api.get('/merchants/me/dashboard'),
  getPaymentLinks:  (params) => api.get('/merchants/me/links', { params }),
  createPaymentLink:(data)   => api.post('/merchants/me/links', data),
  deactivateLink:   (id)     => api.patch(`/merchants/me/links/${id}/disable`),
  getPayments:      (params) => api.get('/merchants/me/payments', { params }),
  getSettlements:   (params) => api.get('/merchants/me/settlements', { params }),
  getSalesReport:   (params) => api.get('/merchants/me/reports', { params }),
  requestSettlement:(data)   => api.post('/merchants/me/settle', data),
  getSettlementConfig:  ()      => api.get('/merchants/me/settlement-config'),
  updateSettlementConfig:(data) => api.patch('/merchants/me/settlement-config', data),
  settlementFeePreview: (amt)   => api.get('/merchants/me/settlement-fee-preview', { params: { amount: amt } }),
};

// ── Notifications ─────────────────────────────────────────────
export const notificationAPI = {
  getAll:         (params) => api.get('/notifications', { params }),
  markRead:       (id)     => api.patch(`/notifications/${id}/read`),     // server: PATCH /:id/read
  markAllRead:    ()       => api.patch('/notifications/mark-all-read'),  // server: PATCH /mark-all-read
  getUnreadCount: ()       => api.get('/notifications/unread-count'),
  deleteOne:      (id)     => api.delete(`/notifications/${id}`),
};

// ── Branches ──────────────────────────────────────────────────
export const branchAPI = {
  getAll:     (params) => api.get('/branches', { params }),
  getOne:     (id)     => api.get(`/branches/${id}`),
  getDashboard:(id)    => api.get(`/branches/${id}/dashboard`),
  getTellers:  (id)    => api.get(`/branches/${id}/tellers`),
  getCash:     (id)    => api.get(`/branches/${id}/cash`),
};

// ── Tellers ───────────────────────────────────────────────────
export const tellerAPI = {
  openSession:   (data)  => api.post('/tellers/sessions/open', data),
  closeSession:  (id)    => api.post(`/tellers/sessions/${id}/close`),
  getMySession:  ()      => api.get('/tellers/sessions/current'),
  getTransactions:(params)=> api.get('/tellers/transactions', { params }),
};

// ── MTN MoMo ──────────────────────────────────────────────────
export const mtnAPI = {
  collect:    (data) => api.post('/mtn-momo/collect', data),
  disburse:   (data) => api.post('/mtn-momo/disburse', data),
  getStatus:  (ref)  => api.get(`/mtn-momo/status/${ref}`),
  getBalance: ()     => api.get('/mtn-momo/balance'),
};

// ── KYC ───────────────────────────────────────────────────────
export const kycAPI = {
  getStatus:  () => api.get('/kyc/status'),
  submit:     (data) => api.post('/kyc/submit', data),
  getHistory: () => api.get('/kyc/history'),
};

// ── AML ───────────────────────────────────────────────────────
export const amlAPI = {
  screen:    (txId) => api.post(`/aml/screen/${txId}`),
  getAlerts: (params) => api.get('/aml/alerts', { params }),
};

// ── Finance Dashboard (admin) ─────────────────────────────────
export const dashboardAPI = {
  getFinance:       ()       => api.get('/dashboard/finance'),
  getOperations:    ()       => api.get('/dashboard/operations'),
  getPending:       ()       => api.get('/dashboard/reconciliation/pending'),
  getLedgerIntegrity:()      => api.get('/dashboard/reconciliation/ledger-integrity'),
  getDailySummary:  (date)   => api.get('/dashboard/reconciliation/daily-summary', { params: { date } }),
  getIpnQueue:      ()       => api.get('/dashboard/reconciliation/ipn-queue'),
  resolveStale:     (dryRun) => api.post(`/dashboard/reconciliation/resolve-stale?dryRun=${dryRun}`),
  retryIpnQueue:    ()       => api.post('/dashboard/reconciliation/retry-ipn-queue'),
};

// ── ISP ───────────────────────────────────────────────────────
export const ispAPI = {
  getRouters:        ()            => api.get('/isp/routers'),
  addRouter:         (data)        => api.post('/isp/routers', data),
  updateRouter:      (id, data)    => api.put(`/isp/routers/${id}`, data),
  deleteRouter:      (id)          => api.delete(`/isp/routers/${id}`),
  toggleRouter:      (id)          => api.post(`/isp/routers/${id}/toggle`),
  getPlans:          ()            => api.get('/isp/plans'),
  createPlan:        (data)        => api.post('/isp/plans', data),
  updatePlan:        (id, data)    => api.put(`/isp/plans/${id}`, data),
  getVouchers:       (params)      => api.get('/isp/vouchers', { params }),
  generateVouchers:  (data)        => api.post('/isp/vouchers/generate', data),
  sellVoucher:       (code, data)  => api.post(`/isp/vouchers/${code}/sell`, data),
  getSubscribers:    (params)      => api.get('/isp/subscribers', { params }),
  getAnalytics:      ()            => api.get('/isp/analytics'),
};

// ── DwiConnect Customer Portal ────────────────────────────────
export const dwiConnectAPI = {
  getConfig:       ()          => api.get('/dwiconnect/config'),
  getDashboard:    ()          => api.get('/dwiconnect/dashboard'),
  getPlans:        (routerId)  => api.get('/dwiconnect/plans', { params: routerId ? { routerId } : {} }),
  getSubscription: ()          => api.get('/dwiconnect/subscription'),
};

// ── ISP Payments (Phase 14) ───────────────────────────────────
export const ispPaymentAPI = {
  // Public (captive portal)
  getPublicPlans:     (routerId) => api.get(routerId ? `/isp/payments/plans/${routerId}/public` : '/isp/payments/plans/public'),
  // Customer
  payWithWallet:      (data)     => api.post('/isp/payments/wallet', data),
  initiateMobileMoney:(data)     => api.post('/isp/payments/mobile-money', data),
  getHistory:         (params)   => api.get('/isp/payments/history', { params }),
  // Admin
  getAllPayments:      (params)   => api.get('/isp/payments/admin/all', { params }),
  getPaymentById:     (id)       => api.get(`/isp/payments/admin/${id}`),
  retryCallback:      (id)       => api.post(`/isp/payments/admin/${id}/retry-callback`),
};

// ── Withdrawals (Phase 16 — Yo! Bulk Payments) ───────────────
export const withdrawalAPI = {
  // Customer
  initiate:        (data)       => api.post('/withdrawals', data),
  quote:           (data)       => api.post('/withdrawals/quote', data),
  getHistory:      ()           => api.get('/withdrawals'),
  getById:         (id)         => api.get(`/withdrawals/${id}`),
  // Admin
  getAll:          (params)     => api.get('/withdrawals/admin/all', { params }),
  getStats:        ()           => api.get('/withdrawals/admin/stats'),
  getAdminById:    (id)         => api.get(`/withdrawals/admin/${id}`),
  approve:         (id)         => api.post(`/withdrawals/admin/${id}/approve`),
  process:         (id)         => api.post(`/withdrawals/admin/${id}/process`),
  reject:          (id, reason) => api.post(`/withdrawals/admin/${id}/reject`, { reason }),
  retry:           (id)         => api.post(`/withdrawals/admin/${id}/retry`),
  cancel:          (id, reason) => api.post(`/withdrawals/admin/${id}/cancel`, { reason }),
  poll:            (id)         => api.post(`/withdrawals/admin/${id}/poll`),
  getStuck:        ()           => api.get('/withdrawals/admin/stuck'),
};

// ── Admin ─────────────────────────────────────────────────────
export const gatewayAPI = {
  listProviders:     (params)      => api.get('/gateway/providers', { params }),
  getProvider:       (id)          => api.get(`/gateway/providers/${id}`),
  registerProvider:  (data)        => api.post('/gateway/providers', data),
  updateProvider:    (id, data)    => api.put(`/gateway/providers/${id}`, data),
  deactivateProvider:(id)          => api.delete(`/gateway/providers/${id}`),
  healthCheck:       (params)      => api.get('/gateway/health', { params }),
  listAdapters:      ()            => api.get('/gateway/adapters'),
  deposit:           (data)        => api.post('/gateway/deposit', data),
  payout:            (data)        => api.post('/gateway/payout', data),
  verifyPayment:     (ref, params) => api.get(`/gateway/payment/${ref}`, { params }),
};

export const adminAPI = {
  getDashboard:        ()           => api.get('/admin/dashboard'),
  getUsers:            (params)     => api.get('/admin/users', { params }),
  suspendUser:         (id, data)   => api.put(`/admin/users/${id}/suspend`, data),
  unsuspendUser:       (id)         => api.put(`/admin/users/${id}/unsuspend`),
  updateUserRole:      (id, data)   => api.put(`/admin/users/${id}/role`, data),
  resetPassword:       (id, data)   => api.put(`/admin/users/${id}/reset-password`, data),
  getKYCQueue:         (params)     => api.get('/admin/kyc', { params }),
  approveKYC:          (id)         => api.put(`/admin/kyc/${id}/approve`),
  rejectKYC:           (id, data)   => api.put(`/admin/kyc/${id}/reject`, data),
  getTransactions:     (params)     => api.get('/admin/transactions', { params }),
  getRevenueReport:    (params)     => api.get('/admin/reports/revenue', { params }),
  getInvestmentReport: ()           => api.get('/admin/reports/investments'),
  approveWithdrawal: (id) => api.put(`/admin/transactions/${id}/approve-withdrawal`),
};

// ── CMS (Admin) ───────────────────────────────────────────────
export const cmsAdminAPI = {
  getCompany:    ()    => api.get('/cms/admin/company'),
  updateCompany: (d)   => api.put('/cms/admin/company', d),
  publish:       ()    => api.post('/cms/admin/company/publish'),
};

// ── Uganda client menu switches (routes/clientModules.js) ──
export const clientModuleAPI = {
  mine:      ()            => api.get('/client-modules'),
  adminList: ()            => api.get('/client-modules/admin'),
  adminSet:  (key, enabled) => api.put(`/client-modules/admin/${key}`, { enabled }),
};

// ── Feature Flags (Admin) ─────────────────────────────────────
export const flagsAdminAPI = {
  getAll:  ()           => api.get('/flags'),
  create:  (data)       => api.post('/flags', data),
  update:  (key, data)  => api.put(`/flags/${key}`, data),
  toggle:  (key)        => api.put(`/flags/${key}/toggle`),
  remove:  (key)        => api.delete(`/flags/${key}`),
};

// ── Limit Rules (Admin) ───────────────────────────────────────
export const limitsAdminAPI = {
  getRules:   (params)     => api.get('/limits/rules', { params }),
  createRule: (data)       => api.post('/limits/rules', data),
  updateRule: (id, data)   => api.put(`/limits/rules/${id}`, data),
  deleteRule: (id)         => api.delete(`/limits/rules/${id}`),
};


// -- Crypto Deposit (Admin)
export const cryptoDepositAPI = {
  getAll:      (params)     => api.get('/crypto-deposit/admin/all', { params }),
  getOne:      (id)         => api.get(`/crypto-deposit/admin/${id}`),
  approve:     (id, data)   => api.post(`/crypto-deposit/admin/${id}/approve`, data),
  reject:      (id, data)   => api.post(`/crypto-deposit/admin/${id}/reject`, data),
  requestInfo: (id, data)   => api.post(`/crypto-deposit/admin/${id}/request-info`, data),
};

// -- Crypto Networks (Admin)
export const cryptoNetworksAPI = {
  getAll:         ()               => api.get('/crypto-networks'),
  create:         (data)           => api.post('/crypto-networks', data),
  update:         (id, data)       => api.put(`/crypto-networks/${id}`, data),
  getWallets:     (netCode)        => api.get(`/crypto-networks/${netCode}/wallets`),
  addWallet:      (netCode, data)  => api.post(`/crypto-networks/${netCode}/wallets`, data),
  updateWallet:   (walletId, data) => api.put(`/crypto-networks/wallets/${walletId}`, data),
  activateWallet: (walletId)       => api.post(`/crypto-networks/wallets/${walletId}/activate`),
};

// -- Investment Projects (Admin)
export const investmentProjectAPI = {
  list:      (params)      => api.get('/investment-projects', { params }),
  adminList: ()            => api.get('/investment-projects/admin/all'),
  getOne:    (id)          => api.get(`/investment-projects/${id}`),
  create:    (data)        => api.post('/investment-projects', data),
  update:    (id, data)    => api.put(`/investment-projects/${id}`, data),
  getPortfolio: ()            => api.get('/investment-projects/portfolio'),
  getDetail:    (id)          => api.get(`/investment-projects/${id}`),
  invest:       (id, data)    => api.post(`/investment-projects/${id}/invest`, data),
};


// -- Treasury Ops (Phase 28)
export const treasuryOpsAPI = {
  getSnapshot:    ()                              => api.get('/treasury-ops/snapshot'),
  getFloat:       ()                              => api.get('/treasury-ops/float'),
  getCryptoWallets: ()                            => api.get('/treasury-ops/crypto-wallets'),
  getReserveRatio:()                              => api.get('/treasury-ops/reserve-ratio'),
  getApprovalQueue: ()                            => api.get('/treasury-ops/approval-queue'),
  getReport:      (period, date)                  => api.get('/treasury-ops/report', { params: { period, date } }),
  getAuditLog:    (params)                        => api.get('/treasury-ops/audit-log', { params }),
};

// -- Crypto Withdrawal (alias)
export const cryptoWithdrawalAPI = {
  getFees:    ()                 => api.get('/crypto-withdraw/fees'),
  getQuote:   (amount, network)  => api.get('/crypto-withdraw/quote', { params: { amount, network } }),
  request:    (data)             => api.post('/crypto-withdraw/request', data),
  getHistory: (params)           => api.get('/crypto-withdraw/history', { params }),
  getQueue:   (params)           => api.get('/crypto-withdraw/queue', { params }),
  approve:    (id)               => api.post(`/crypto-withdraw/${id}/approve`),
  markSent:   (id, data)         => api.post(`/crypto-withdraw/${id}/mark-sent`, data),
  complete:   (id)               => api.post(`/crypto-withdraw/${id}/complete`),
  reject:     (id, data)         => api.post(`/crypto-withdraw/${id}/reject`, data),
};

// -- P2P Reference Rates (Phase 43)
export const p2pRatesAPI = {
  getCurrent:      ()       => api.get('/p2p-rates/current'),
  setAcquisition:  (data)   => api.put('/p2p-rates/acquisition', data),
  setDisposal:     (data)   => api.put('/p2p-rates/disposal', data),
  getHistory:      (params) => api.get('/p2p-rates/history', { params }),
  getConfig:       ()       => api.get('/p2p-rates/config'),
  updateConfig:    (data)   => api.put('/p2p-rates/config', data),
  getDailySummary: (params) => api.get('/p2p-rates/daily-summary', { params }),
};


// -- Phase 44: Merchant Charges API + API Key management
export const merchantChargesAPI = {
  // Charges (API key auth — calls v1 endpoints via same axios instance with JWT)
  listCharges:   (params) => api.get('/merchants/me/charges', { params }),
  getCharge:     (id)     => api.get(`/merchants/me/charges/${id}`),

  // API Keys (JWT auth)
  listApiKeys:   ()       => api.get('/merchants/me/api-keys'),
  createApiKey:  (data)   => api.post('/merchants/me/api-keys', data),
  revokeApiKey:  (id)     => api.delete(`/merchants/me/api-keys/${id}`),

  // Webhook config (JWT auth — merchant dashboard routes)
  getWebhookConfig:    ()     => api.get('/merchants/me/webhooks/config'),
  updateWebhookConfig: (data) => api.put('/merchants/me/webhooks/config', data),
  getWebhookEvents:    (p)    => api.get('/merchants/me/webhooks/events', { params: p }),

  // Fee tables (admin)
  listFeeTables:   (name)   => api.get('/fee-tables', { params: name ? { name } : {} }),
  createFeeTable:  (data)   => api.post('/fee-tables', data),
  updateFeeTable:  (id, d)  => api.patch(`/fee-tables/${id}`, d),
  computeFee:      (params) => api.get('/fee-tables/compute', { params }),
};


// -- Phase 48: Settlement Engine
export const settlementAPI = {
  list:          (params) => api.get('/settlements/me', { params }),
  getConfig:     ()       => api.get('/settlements/me/config'),
  updateConfig:  (data)   => api.patch('/settlements/me/config', data),
  feePreview:    (amount) => api.get('/settlements/me/fee-preview', { params: { amount } }),
  request:       ()       => api.post('/settlements/me/request'),
  listAll:       (params) => api.get('/settlements', { params }),
  adminTrigger:  (mid)    => api.post('/settlements/admin/trigger/' + mid),
  retry:         (stlId)  => api.post('/settlements/' + stlId + '/retry'),
};

// -- Phase 48: Invoice API
export const invoiceAPI = {
  list:         (params) => api.get('/invoices', { params }),
  create:       (data)   => api.post('/invoices', data),
  get:          (id)     => api.get('/invoices/' + id),
  update:       (id, d)  => api.patch('/invoices/' + id, d),
  send:         (id)     => api.post('/invoices/' + id + '/send'),
  cancel:       (id)     => api.post('/invoices/' + id + '/cancel'),
  getPublic:    (id)     => api.get('/charge/i/' + id),
  ensureCharge: (id)     => api.post('/charge/i/' + id + '/charge'),
};

// -- Phase 48: Static Merchant QR API
export const staticQrAPI = {
  getMerchant:  (code)      => api.get('/charge/m/' + code),
  createCharge: (code, data) => api.post('/charge/m/' + code, data),
};

export const ispPackageAPI = {
  list:        ()                     => api.get('/isp-packages'),
  purchase:    (packageId, phone, ispIdentity) => api.post('/isp-packages/purchase', { packageId, phone, ispIdentity }),
  myPurchases: (page = 1, limit = 10) => api.get('/isp-packages/my-purchases', { params: { page, limit } }),
  // Admin
  adminList:   ()                     => api.get('/isp-packages/admin/packages'),
  create:      (data)                 => api.post('/isp-packages/admin/packages', data),
  update:      (id, data)             => api.patch('/isp-packages/admin/packages/' + id, data),
  uploadCodes: (id, codes)            => api.post('/isp-packages/admin/packages/' + id + '/upload-codes', { codes }),
  getStock:    (id)                   => api.get('/isp-packages/admin/packages/' + id + '/stock'),
  adminPurchases: (params)            => api.get('/isp-packages/admin/purchases', { params }),
  previewCodes:   (codes)             => api.post('/isp-packages/admin/preview-codes', { codes }),
  // Monthly-client self-service — manual_activation
  adminActivations:  ()               => api.get('/isp-packages/admin/activations'),
  activatePurchase:  (purchaseId)     => api.post('/isp-packages/admin/activations/' + purchaseId + '/activate'),
  createImport:      (data)           => api.post('/isp-packages/admin/imports', data),
  previewImportCsv:  (rows)           => api.post('/isp-packages/admin/imports/preview-csv', { rows }),
  bulkImport:        (rows)           => api.post('/isp-packages/admin/imports/bulk', { rows }),
  adminImports:      (params)         => api.get('/isp-packages/admin/imports', { params }),
  updateImportDates: (id, data)       => api.patch('/isp-packages/admin/imports/' + id, data),
  linkExisting:      (data)           => api.post('/isp-packages/link-existing', data),
};

// ── DWIFI Captive Portal (Tier 1) — public, no auth header needed ────────────
export const captivePortalAPI = {
  identify:        (phone)                         => api.post('/captive-portal/identify', { phone }),
  signup:          (data)                           => api.post('/captive-portal/signup', data),
  // login and buyWithWallet verify a phone+PIN credential and can
  // legitimately 401 on a wrong PIN — skipAuthRedirect stops that from
  // being mistaken for an expired session (see the interceptor above).
  login:           (data)                           => api.post('/captive-portal/login', data, { skipAuthRedirect: true }),
  listPackages:    (phone)                          => api.get('/captive-portal/packages', { params: phone ? { phone } : {} }),
  buyWithWallet:   (data)                           => api.post('/captive-portal/buy/wallet', data, { skipAuthRedirect: true }),
  redeemVoucher:   (data)                           => api.post('/captive-portal/redeem-voucher', data),
  // 100s, not the 15s default: the server may wait on Yo! (~15-90s) when
  // captive_portal_async_collection is OFF; with it ON this returns at once
  buyWithMobileMoney: (data)                        => api.post('/captive-portal/buy/mobile-money', data, { timeout: 100000 }),
  getStatus:       (reference)                      => api.get(`/captive-portal/status/${reference}`),
  // Admin
  seedPackages:    ()                               => api.post('/captive-portal/admin/seed-packages'),
};

// -- Launch Event RSVP -------------------------------------------------------
export const eventRegistrationAPI = {
  getStatus:    ()             => api.get('/launch-event/status'),
  register:     (data)         => api.post('/launch-event/register', data),
  adminList:    (params)       => api.get('/launch-event/admin/registrations', { params }),
  adminCounts:  ()             => api.get('/launch-event/admin/counts'),
  checkIn:      (id)           => api.patch('/launch-event/admin/registrations/' + id + '/checkin'),
  undoCheckIn:  (id)           => api.patch('/launch-event/admin/registrations/' + id + '/undo-checkin'),
};

// -- Partnership & Network Management (Phase 52A) ---------------------------
export const partnershipAPI = {
  getStatus:   ()                      => api.get('/partnership/status'),
  getInvitePreview: (token)                => api.get(`/partnership/invite/${token}`),
  acceptInvite:(inviteToken, newPassword) => api.post('/partnership/invite/accept', { inviteToken, newPassword }),

  // Admin
  adminStats:        ()                        => api.get('/partnership/admin/stats'),
  listProjects:      ()                        => api.get('/partnership/admin/projects'),
  getProject:        (id)                      => api.get(`/partnership/admin/projects/${id}`),
  createProject:     (data)                    => api.post('/partnership/admin/projects', data),
  updateProject:     (id, data)                => api.patch(`/partnership/admin/projects/${id}`, data),
  listSites:         (params)                  => api.get('/partnership/admin/sites', { params }),
  getSite:           (id)                      => api.get(`/partnership/admin/sites/${id}`),
  createSite:        (data)                    => api.post('/partnership/admin/sites', data),
  updateSite:        (id, data)                => api.patch(`/partnership/admin/sites/${id}`, data),
  suggestFundingCost:(id)                      => api.get(`/partnership/admin/sites/${id}/suggest-funding-cost`),
  assignPartner:     (siteId, data)            => api.post(`/partnership/admin/sites/${siteId}/assign-partner`, data),
  unassignPartner:   (siteId)                  => api.post(`/partnership/admin/sites/${siteId}/unassign-partner`),
  advanceSiteStatus: (siteId, status)          => api.post(`/partnership/admin/sites/${siteId}/advance-status`, { status }),
  postProgress:      (siteId, data)            => api.post(`/partnership/admin/sites/${siteId}/progress`, data),
  listProgress:      (siteId)                  => api.get(`/partnership/admin/sites/${siteId}/progress`),
  checkPartnerDuplicate: (data)                => api.post('/partnership/admin/partners/check-duplicate', data),
  invitePartner:     (data)                    => api.post('/partnership/admin/partners/invite', data),
  resendInvite:      (id)                      => api.post(`/partnership/admin/partners/${id}/resend`),
  listPartners:      ()                        => api.get('/partnership/admin/partners'),
  getPartner:        (id)                      => api.get(`/partnership/admin/partners/${id}`),
  deletePartner:     (id, force = false)       => api.delete(`/partnership/admin/partners/${id}`, { data: { force } }),
  revokeInvite:      (id, force = false)       => api.post(`/partnership/admin/partners/${id}/revoke`, { force }),
  addEquipment:      (data)                    => api.post('/partnership/admin/equipment', data),
  listEquipment:     (params)                  => api.get('/partnership/admin/equipment', { params }),
  updateEquipment:   (id, data)                => api.patch(`/partnership/admin/equipment/${id}`, data),
  deleteEquipment:   (id)                      => api.delete(`/partnership/admin/equipment/${id}`),
  updateEquipmentStatus: (id, status)          => api.patch(`/partnership/admin/equipment/${id}/status`, { status }),
  assignEquipment:   (id, siteId)              => api.post(`/partnership/admin/equipment/${id}/assign`, { siteId }),
  attributePurchase: (purchaseId, siteId)      => api.post(`/partnership/admin/purchases/${purchaseId}/attribute`, { siteId }),
  syncRevenue:       (siteId, periodStart, periodEnd) => api.post(`/partnership/admin/sites/${siteId}/revenue/sync`, { periodStart, periodEnd }),
  getSiteRevenue:    (siteId)                  => api.get(`/partnership/admin/sites/${siteId}/revenue`),
  getSiteEarnings:   (siteId)                  => api.get(`/partnership/admin/sites/${siteId}/earnings`),

  // Capacity management + upgrade tracking
  getSiteCapacity:   (siteId)                  => api.get(`/partnership/admin/sites/${siteId}/capacity`),
  listSiteUpgrades:  (siteId)                  => api.get(`/partnership/admin/sites/${siteId}/upgrades`),
  recordUpgrade:     (siteId, data)            => api.post(`/partnership/admin/sites/${siteId}/upgrades`, data),
  getStandardDeploymentTemplate: ()            => api.get('/partnership/admin/standard-deployment-template'),
  applyStandardDeployment: (siteId)            => api.post(`/partnership/admin/sites/${siteId}/apply-standard-deployment`),
  attributeRadiusUserToSite: (radiusUserId, siteId) => api.post(`/partnership/admin/radius-users/${radiusUserId}/attribute-site`, { siteId }),

  // Site cost tracking + net-earnings ("Model B")
  recordSiteCost:    (siteId, data)            => api.post(`/partnership/admin/sites/${siteId}/costs`, data),
  listSiteCosts:     (siteId, period)          => api.get(`/partnership/admin/sites/${siteId}/costs`, { params: period ? { period } : {} }),
  getSiteMonthlyEarnings: (siteId, period)     => api.get(`/partnership/admin/sites/${siteId}/earnings/monthly`, { params: period ? { period } : {} }),
  syncSiteNetEarnings: (siteId, period)        => api.post(`/partnership/admin/sites/${siteId}/earnings/sync`, { period }),
  getAdminEarningsSummary: (period)            => api.get('/partnership/admin/earnings/summary', { params: period ? { period } : {} }),
  attributeCaptivePortalPurchaseToSite: (purchaseId, siteId) => api.post(`/partnership/admin/captive-portal-purchases/${purchaseId}/attribute-site`, { siteId }),
  getUnattributedCaptivePortalPurchases: (page, limit) => api.get('/partnership/admin/captive-portal-purchases/needs-attribution', { params: { page, limit } }),

  // Geographic hierarchy (Country -> Region -> District -> Division -> Parish -> Village -> Community -> Site)
  getGeoLevels:      ()                        => api.get('/partnership/admin/geo/levels'),
  createGeoUnit:     (data)                    => api.post('/partnership/admin/geo/units', data),
  listGeoUnits:      (params)                  => api.get('/partnership/admin/geo/units', { params }),
  getGeoUnit:        (id)                      => api.get(`/partnership/admin/geo/units/${id}`),
  getGeoCounts:      (level)                   => api.get('/partnership/admin/geo/counts', { params: { level } }),
  assignSiteGeo:     (siteId, geoUnitId)        => api.post(`/partnership/admin/sites/${siteId}/geo`, { geoUnitId }),

  // Partner portal
  portalMe:          ()          => api.get('/partnership/portal/me'),
  portalSites:       ()          => api.get('/partnership/portal/sites'),
  portalSite:        (id)        => api.get(`/partnership/portal/sites/${id}`),
  portalSiteProgress:(id)        => api.get(`/partnership/portal/sites/${id}/progress`),
  portalSitePerformance: (id)    => api.get(`/partnership/portal/sites/${id}/performance`),
  portalSiteEquipment: (id)      => api.get(`/partnership/portal/sites/${id}/equipment`),
  portalSiteUpgrades: (id)       => api.get(`/partnership/portal/sites/${id}/upgrades`),
  portalSiteMonthlyEarnings: (id, period) => api.get(`/partnership/portal/sites/${id}/earnings/monthly`, { params: period ? { period } : {} }),
  portalEarnings:    ()          => api.get('/partnership/portal/earnings'),
  portalFinancialSummary: ()     => api.get('/partnership/portal/financial-summary'),
  portalMoveEarningsToWallet: () => api.post('/partnership/portal/earnings/move-to-wallet'),

  // Leads (Partner Interest) — public
  leadStatus:        ()                        => api.get('/partnership/lead-status'),
  submitLead:        (data)                    => api.post('/partnership/leads', data),
  // Leads — admin
  listLeads:         (params)                  => api.get('/partnership/admin/leads', { params }),
  getLead:           (id)                      => api.get(`/partnership/admin/leads/${id}`),
  updateLead:        (id, data)                => api.patch(`/partnership/admin/leads/${id}`, data),
  convertLead:        (id, data)               => api.post(`/partnership/admin/leads/${id}/convert`, data),
};

// -- Network (Tier 1 — MikroTik/RADIUS router registration) -----------------
export const networkAPI = {
  listRouters:   ()            => api.get('/network/routers'),
  getRouter:     (id)          => api.get(`/network/routers/${id}`),
  registerRouter:(data)        => api.post('/network/routers', data),
  updateRouter:  (id, data)    => api.patch(`/network/routers/${id}`, data),
  linkSite:      (id, siteId)  => api.post(`/network/routers/${id}/link-site`, { siteId }),
  linkEquipment: (id, equipmentAssetId) => api.post(`/network/routers/${id}/link-equipment`, { equipmentAssetId }),
  generateConfig: (id, data) => api.post(`/network/routers/${id}/generate-config`, data),
  generatePortalAddOn: (id) => api.post(`/network/routers/${id}/generate-portal-addon`),

  // Router control (Phase 1 — read-only status; admin/system only)
  enableRouterApi:  (id) => api.post(`/network/routers/${id}/api/enable`),
  getRouterApiStatus: (id) => api.get(`/network/routers/${id}/api/status`),
  getFleetHealth: () => api.get('/network/routers/fleet-health'),

  // Self-provisioning — issues a one-line /tool fetch + /import command
  generateProvisionToken: (id, data) => api.post(`/network/routers/${id}/generate-provision-token`, data),

  // Partner portal — read-only
  portalSiteRouter: (siteId)   => api.get(`/network/portal/sites/${siteId}/router`),
};

// -- Country Management (Phase 30) -----------------------------------------
export const countryAPI = {
  list:       ()             => api.get('/countries'),
  getDefault: ()             => api.get('/countries/default'),
  getByCode:  (code)         => api.get(`/countries/${code}`),
  adminList:  ()             => api.get('/countries/admin/all'),
  adminGet:   (id)           => api.get(`/countries/admin/${id}`),
  create:     (data)         => api.post('/countries/admin', data),
  update:     (id, data)     => api.put(`/countries/admin/${id}`, data),
  setActive:  (id, isActive) => api.patch(`/countries/admin/${id}/active`, { isActive }),
  remove:     (id)           => api.delete(`/countries/admin/${id}`),
};

// ── DwiSend offline payments, two-person check (routes/dwisendPayments.js) ──
// Admins and payment approvers (approvers need not be admins).
export const dwisendPaymentAPI = {
  status:  ()               => api.get('/dwisend/payments/status'),
  list:    (status)         => api.get('/dwisend/payments', { params: { status } }),
  get:     (id)             => api.get(`/dwisend/payments/${id}`),
  approve: (id, data)       => api.post(`/dwisend/payments/${id}/approve`, data),
  reject:  (id, reason)     => api.post(`/dwisend/payments/${id}/reject`, { reason }),
  record:  (data)           => api.post('/dwisend/payments', data),   // admin
};

// ── DwiSend requests — DRC clients, USD (routes/dwisendRequests.js) ──
export const dwisendRequestAPI = {
  status:      ()             => api.get('/dwisend/requests/status'),
  setLanguage: (language)     => api.put('/dwisend/requests/language', { language }),
  mine:        ()             => api.get('/dwisend/requests/mine'),
  create:      (data)         => api.post('/dwisend/requests', data),
  cancel:      (id)           => api.post(`/dwisend/requests/${id}/cancel`),
  accept:      (quoteId)      => api.post(`/dwisend/requests/quotes/${quoteId}/accept`),
  reject:      (quoteId, note) => api.post(`/dwisend/requests/quotes/${quoteId}/reject`, { note }),
  // admin
  adminList:   (params)       => api.get('/dwisend/requests/admin', { params }),
  adminGet:    (id)           => api.get(`/dwisend/requests/admin/${id}`),
  adminNotes:  (id, adminNotes) => api.put(`/dwisend/requests/admin/${id}/notes`, { adminNotes }),
  adminStatus: (id, status, reason) => api.post(`/dwisend/requests/admin/${id}/status`, { status, reason }),
  adminQuote:  (id, data)     => api.post(`/dwisend/requests/admin/${id}/quotes`, data),
  adminWithdrawQuote: (quoteId) => api.post(`/dwisend/requests/admin/quotes/${quoteId}/withdraw`),
};

// ── DwiSend shop — DRC clients, USD, paid offline (routes/dwisendShop.js) ──
export const dwisendShopAPI = {
  status:      ()             => api.get('/dwisend/shop/status'),
  mine:        ()             => api.get('/dwisend/shop/mine'),
  order:       (data)         => api.post('/dwisend/shop/orders', data),
  cancel:      (id)           => api.post(`/dwisend/shop/orders/${id}/cancel`),
  // admin (country defaults to CD)
  adminSettings:     ()             => api.get('/dwisend/shop/admin/settings'),
  adminSaveSettings: (data)         => api.put('/dwisend/shop/admin/settings', data),
  adminProducts:     ()             => api.get('/dwisend/shop/admin/products'),
  adminCreateProduct:(data)         => api.post('/dwisend/shop/admin/products', data),
  adminUpdateProduct:(id, data)     => api.put(`/dwisend/shop/admin/products/${id}`, data),
  adminStock:        (id, data)     => api.post(`/dwisend/shop/admin/products/${id}/stock`, data),
  adminConvert:      (params)       => api.get('/dwisend/shop/admin/convert', { params }),   // suggestion only — saves nothing
  adminProductHistory: (id)         => api.get(`/dwisend/shop/admin/products/${id}/history`),
  adminOrders:       (params)       => api.get('/dwisend/shop/admin/orders', { params }),
  adminOrder:        (id)           => api.get(`/dwisend/shop/admin/orders/${id}`),
  adminOrderStatus:  (id, data)     => api.post(`/dwisend/shop/admin/orders/${id}/status`, data),
  adminOrderNotes:   (id, adminNotes) => api.put(`/dwisend/shop/admin/orders/${id}/notes`, { adminNotes }),
  adminDeliveryFee:  (id, fee)      => api.put(`/dwisend/shop/admin/orders/${id}/delivery-fee`, { fee }),
};

// ── DwiSend cities per country (routes/dwisendCities.js) — admin only ──
export const dwisendCityAPI = {
  status:    ()             => api.get('/dwisend/cities/status'),
  setSwitch: (enabled)      => api.put('/dwisend/cities/switch', { enabled }),
  list:      (countryCode)  => api.get('/dwisend/cities', { params: { countryCode } }),
  create:    (data)         => api.post('/dwisend/cities', data),
  update:    (id, data)     => api.put(`/dwisend/cities/${id}`, data),
  remove:    (id)           => api.delete(`/dwisend/cities/${id}`),
};

// ── DwiSend logistics — Phase 1, records only (routes/dwisendLogistics.js) ──
// Admin only, except approvals (admins + DwiSend approvers). Moves no money.
export const dwisendLogisticsAPI = {
  status:      ()             => api.get('/dwisend/logistics/status'),
  setSwitch:   (enabled)      => api.put('/dwisend/logistics/switch', { enabled }),
  approvals:   ()             => api.get('/dwisend/logistics/approvals'),
  approveActivation: (id, data) => api.post(`/dwisend/logistics/routes/${id}/approve-activation`, data),
  rejectActivation:  (id, reason) => api.post(`/dwisend/logistics/routes/${id}/reject-activation`, { reason }),
  approvePaid: (id, data)     => api.post(`/dwisend/logistics/ledger/${id}/approve-paid`, data),
  rejectPaid:  (id, reason)   => api.post(`/dwisend/logistics/ledger/${id}/reject-paid`, { reason }),

  locations:      (params)    => api.get('/dwisend/logistics/locations', { params }),
  createLocation: (data)      => api.post('/dwisend/logistics/locations', data),
  updateLocation: (id, data)  => api.put(`/dwisend/logistics/locations/${id}`, data),
  partners:       ()          => api.get('/dwisend/logistics/partners'),
  createPartner:  (data)      => api.post('/dwisend/logistics/partners', data),
  updatePartner:  (id, data)  => api.put(`/dwisend/logistics/partners/${id}`, data),
  createVehicle:  (partnerId, data) => api.post(`/dwisend/logistics/partners/${partnerId}/vehicles`, data),
  updateVehicle:  (id, data)  => api.put(`/dwisend/logistics/vehicles/${id}`, data),
  routes:         (params)    => api.get('/dwisend/logistics/routes', { params }),
  createRoute:    (data)      => api.post('/dwisend/logistics/routes', data),
  updateRoute:    (id, data)  => api.put(`/dwisend/logistics/routes/${id}`, data),
  setCheck:       (id, data)  => api.post(`/dwisend/logistics/routes/${id}/checks`, data),
  activateRoute:  (id)        => api.post(`/dwisend/logistics/routes/${id}/activate`),
  deactivateRoute:(id, reason) => api.post(`/dwisend/logistics/routes/${id}/deactivate`, { reason }),
  dispatches:     (params)    => api.get('/dwisend/logistics/dispatches', { params }),
  createDispatch: (data)      => api.post('/dwisend/logistics/dispatches', data),
  dispatchStatus: (id, data)  => api.post(`/dwisend/logistics/dispatches/${id}/status`, data),
  ledger:         (params)    => api.get('/dwisend/logistics/ledger', { params }),
  addEntry:       (data)      => api.post('/dwisend/logistics/ledger', data),
  markPaid:       (id, data)  => api.post(`/dwisend/logistics/ledger/${id}/mark-paid`, data),
  reverseEntry:   (id, reason) => api.post(`/dwisend/logistics/ledger/${id}/reverse`, { reason }),
};

// ── DWI Market (routes/market.js) ─────────────────────────────
export const marketAPI = {
  getStatus:      ()               => api.get('/market/status'),
  // Buyer
  getShop:        ()               => api.get('/market/products'),
  placeOrder:     (data)           => api.post('/market/orders', data),
  myOrders:       (params)         => api.get('/market/orders/mine', { params }),
  getOrder:       (id)             => api.get(`/market/orders/${id}`),
  syncPayment:    (id)             => api.post(`/market/orders/${id}/sync-payment`),
  setStatus:      (id, status, note, delivery) => api.post(`/market/orders/${id}/status`, { status, note, delivery }),
  // Public marketing page /dwi-market (no login)
  getLanding:     ()               => api.get('/market/landing'),
  publicCatalog:  ()               => api.get('/market/public/catalog'),
  publicProduct:  (slug)           => api.get(`/market/public/products/${encodeURIComponent(slug)}`),
  previewCatalog: ()               => api.get('/market/admin/public-preview'),
  previewProduct: (slug)           => api.get(`/market/admin/public-preview/${encodeURIComponent(slug)}`),
  // Messages — users only ever talk to DWI
  getContact:     ()               => api.get('/market/contact'),
  myThreads:      ()               => api.get('/market/threads'),
  startThread:    (data)           => api.post('/market/threads', data),
  getThread:      (id, after)      => api.get(`/market/threads/${id}`, { params: after ? { after } : undefined }),
  sendMessage:    (id, body, photo) => api.post(`/market/threads/${id}/messages`, { body, photo }),
  // Phase 5: unread badges, private chat photos
  unread:         ()               => api.get('/market/unread'),
  chatPhotoUpload:(file)           => { const f = new FormData(); f.append('photo', file); return api.post('/market/threads/photos', f, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }); },
  chatPhoto:      (name)           => api.get(`/market/chat-photos/${encodeURIComponent(name)}`, { responseType: 'blob', timeout: 30000 }),
  // Admin
  getSettings:    ()               => api.get('/market/admin/settings'),
  saveSettings:   (data)           => api.put('/market/admin/settings', data),
  listCatalog:    (kind)           => api.get(`/market/admin/catalog/${kind}`),
  createCatalog:  (kind, data)     => api.post(`/market/admin/catalog/${kind}`, data),
  updateCatalog:  (kind, id, data) => api.put(`/market/admin/catalog/${kind}/${id}`, data),
  listProducts:   ()               => api.get('/market/admin/products'),
  createProduct:  (data)           => api.post('/market/admin/products', data),
  updateProduct:  (id, data)       => api.put(`/market/admin/products/${id}`, data),
  changeStock:    (id, data)       => api.post(`/market/admin/products/${id}/stock`, data),
  uploadPhoto:    (file)           => { const f = new FormData(); f.append('photo', file); return api.post('/market/admin/photos', f, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }); },
  adminOrders:    (params)         => api.get('/market/admin/orders', { params }),
  markPaidOffline:(id, note)       => api.post(`/market/admin/orders/${id}/mark-paid-offline`, { note }),
  setDelivery:    (id, data)       => api.put(`/market/admin/orders/${id}/delivery`, data),
  landingStats:   (days)           => api.get('/market/admin/landing-stats', { params: { days } }),
  adminThreads:   (params)         => api.get('/market/admin/threads', { params }),
  adminThread:    (id, after)      => api.get(`/market/admin/threads/${id}`, { params: after ? { after } : undefined }),
  adminReply:     (id, body, photo) => api.post(`/market/admin/threads/${id}/messages`, { body, photo }),
  adminStartThread: (data)         => api.post('/market/admin/threads', data),
  adminChatPhotoUpload: (file)     => { const f = new FormData(); f.append('photo', file); return api.post('/market/admin/threads/photos', f, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }); },
  adminUsers:     (q)              => api.get('/market/admin/users', { params: { q } }),
  // Phase 6: dashboard, margin report, seller payables (records only — no money moves)
  dashboard:      ()               => api.get('/market/admin/dashboard'),
  readiness:      ()               => api.get('/market/admin/readiness'),
  marginReport:   (params)         => api.get('/market/admin/reports/margin', { params }),
  payables:       (params)         => api.get('/market/admin/payables', { params }),
  addPayable:     (data)           => api.post('/market/admin/payables', data),
  payPayables:    (data)           => api.post('/market/admin/payables/pay', data),
  setPayableAmount: (id, amountUGX) => api.put(`/market/admin/payables/${id}/amount`, { amountUGX }),
  cancelPayable:  (id, reason)     => api.post(`/market/admin/payables/${id}/cancel`, { reason }),
  adminThreadStatus: (id, status)  => api.post(`/market/admin/threads/${id}/status`, { status }),
  // Sell to DWI (Phase 2) — seller's own application + listings. Photos are private:
  // fetched with the login token as a blob (supplyPhoto), never a public URL.
  sellMe:         ()               => api.get('/market/sell/me'),
  sellApply:      (data)           => api.post('/market/sell/apply', data),
  sellCreate:     (data)           => api.post('/market/sell/listings', data),
  sellUpdate:     (id, data)       => api.put(`/market/sell/listings/${id}`, data),
  sellAction:     (id, action)     => api.post(`/market/sell/listings/${id}/${action}`),
  sellPhoto:      (file)           => { const f = new FormData(); f.append('photo', file); return api.post('/market/sell/photos', f, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }); },
  supplyPhoto:    (name)           => api.get(`/market/supply-photos/${encodeURIComponent(name)}`, { responseType: 'blob', timeout: 30000 }),
  // Admin: sellers + supply catalog
  adminSellers:   (params)         => api.get('/market/admin/sellers', { params }),
  reviewSeller:   (id, action, reason) => api.post(`/market/admin/sellers/${id}/review`, { action, reason }),
  sellerNotes:    (id, adminNotes) => api.put(`/market/admin/sellers/${id}/notes`, { adminNotes }),
  adminSupply:    (params)         => api.get('/market/admin/supply', { params }),
  reviewListing:  (id, action, reason) => api.post(`/market/admin/supply/${id}/review`, { action, reason }),
  listingNotes:   (id, data)       => api.put(`/market/admin/supply/${id}/notes`, data),
  // Admin direct-edit of a seller listing (dryRun: true previews); history; objection review
  adminEditListing:  (id, data)     => api.post(`/market/admin/supply/${id}/edit`, data),
  adminListingEdits: (id)           => api.get(`/market/admin/supply/${id}/edits`),
  reviewObjection:   (editId, note) => api.post(`/market/admin/supply/edits/${editId}/review`, { note }),
  objectToEdit:      (editId, reason) => api.post(`/market/sell/edits/${editId}/object`, { reason }),   // seller: never undoes the edit
  publishListing: (id, data)       => api.post(`/market/admin/supply/${id}/publish`, data),
  // Buy requests + quotes (Phase 3)
  myRequests:     ()               => api.get('/market/requests/mine'),
  createRequest:  (data)           => api.post('/market/requests', data),
  cancelRequest:  (id)             => api.post(`/market/requests/${id}/cancel`),
  acceptQuote:    (id, data)       => api.post(`/market/quotes/${id}/accept`, data),
  declineQuote:   (id, note)       => api.post(`/market/quotes/${id}/decline`, { note }),
  adminRequests:  (params)         => api.get('/market/admin/requests', { params }),
  adminRequest:   (id)             => api.get(`/market/admin/requests/${id}`),
  requestStatus:  (id, status, reason) => api.post(`/market/admin/requests/${id}/status`, { status, reason }),
  requestNotes:   (id, adminNotes) => api.put(`/market/admin/requests/${id}/notes`, { adminNotes }),
  sendQuote:      (id, data)       => api.post(`/market/admin/requests/${id}/quotes`, data),
  withdrawQuote:  (id)             => api.post(`/market/admin/quotes/${id}/withdraw`),
};

export default api;

// ── DWI SocialHub (routes/socialhub.js) ───────────────────────
export const socialHubAPI = {
  getStatus:      ()               => api.get('/socialhub/status'),
  getBrand:       ()               => api.get('/socialhub/brand'),
  saveBrand:      (data)           => api.put('/socialhub/brand', data),
  startLogo:      (data)           => api.post('/socialhub/brand/logo', data),
  confirmLogo:    (key)            => api.post('/socialhub/brand/logo/confirm', { key }),
  listItems:      (params)         => api.get('/socialhub/items', { params }),
  createItem:     (data)           => api.post('/socialhub/items', data),
  getItem:        (id)             => api.get(`/socialhub/items/${id}`),
  confirmUpload:  (id)             => api.post(`/socialhub/items/${id}/uploaded`),
  updateItem:     (id, data)       => api.patch(`/socialhub/items/${id}`, data),
  redoCaptions:   (id)             => api.post(`/socialhub/items/${id}/captions`),
  archiveItem:    (id)             => api.delete(`/socialhub/items/${id}`),
  schedule:       (id, data)       => api.post(`/socialhub/items/${id}/posts`, data),
  listPosts:      (params)         => api.get('/socialhub/posts', { params }),
  cancelPost:     (id)             => api.post(`/socialhub/posts/${id}/cancel`),
  resolvePost:    (id, outcome, permalink) => api.post(`/socialhub/posts/${id}/resolve`, { outcome, permalink }),
  usage:          ()               => api.get('/socialhub/usage'),
  listAccounts:   ()               => api.get('/socialhub/accounts'),
  connectMeta:    ()               => api.post('/socialhub/accounts/meta/connect'),
  setAccount:     (id, enabled)    => api.patch(`/socialhub/accounts/${id}`, { enabled }),
  disconnect:     (id)             => api.delete(`/socialhub/accounts/${id}`),
};

// Photo/logo bytes go straight from the browser to R2 with the signed URL the
// API hands out — not through the API (no auth header, no 15 s timeout).
export async function uploadToSignedUrl(url, file) {
  const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
  if (!res.ok) throw new Error(`Upload failed (${res.status}) — please try again`);
}
